import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff, Loader2, Lock, Mail, ShieldCheck, User } from "lucide-react";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { api, apiError, fileUrl } from "@/lib/api";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";
import { useSeo } from "@/lib/seo";

const AUTH_PANEL_FALLBACK = {
  enabled: true,
  image_path: "",
  title: "Vos licences officielles",
  title_highlight: "au meilleur prix",
  subtitle:
    "Retrouvez toutes vos clés, factures et abonnements dans un espace client sécurisé. Livraison instantanée, support 7j/7.",
  bullets: [
    "Toutes vos licences centralisées",
    "Factures téléchargeables",
    "Tickets support suivis",
    "Favoris synchronisés",
  ],
};

function AuthLogo({ light = false, large = false }) {
  return (
    <Link to="/" data-testid="auth-logo" className="group inline-flex items-center gap-3.5">
      <span
        className="overflow-hidden rounded-2xl shadow-lg shadow-brand/25 ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-[1.04]"
        style={{ backgroundColor: "#000307" }}
      >
        <img
          src="/logo-it.png"
          alt="ISMO-TECH"
          width="455"
          height="331"
          className={`w-auto object-contain ${large ? "h-16 sm:h-20" : "h-14 sm:h-16"}`}
        />
      </span>
      <span className="flex flex-col leading-none">
        <span className={`font-display text-xl font-extrabold tracking-tight ${light ? "text-white" : "text-ink"}`}>
          ISMO<span className="text-brand">-TECH</span>
        </span>
        <span className="mt-1 text-[9px] font-bold uppercase tracking-[0.22em] text-slate-400">
          Multiples Services Informatiques
        </span>
      </span>
    </Link>
  );
}

function AuthShell({ title, subtitle, children, footer }) {
  const [panelData, setPanelData] = useState(null);
  useEffect(() => {
    let active = true;
    api.get("/settings/auth-panel")
      .then(({ data }) => active && setPanelData(data))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const panel = panelData || AUTH_PANEL_FALLBACK;
  const showPanel = panel.enabled !== false;

  return (
    <div className={showPanel ? "grid min-h-[calc(100vh-140px)] lg:grid-cols-2" : "flex min-h-[calc(100vh-140px)] items-center justify-center"}>
      <div className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <AuthLogo />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-ink sm:text-3xl">{title}</h1>
          <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
          {children}
          {footer}
        </div>
      </div>

      {showPanel && (
        <div className="hero-shell relative hidden overflow-hidden lg:block" data-testid="auth-side-panel">
          {panel.image_path && (
            <>
              <img
                src={fileUrl(panel.image_path)}
                alt=""
                data-testid="auth-panel-bg"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#020617]/90 via-[#020617]/75 to-brand/50" />
            </>
          )}
          <div className="hero-grid-lines absolute inset-0 opacity-60" />
          <div className="relative z-10 flex h-full flex-col justify-center px-14">
            <AuthLogo light large />
            <h2 className="mt-10 max-w-md font-display text-4xl font-extrabold leading-tight text-white" data-testid="auth-panel-title">
              {panel.title} <span className="text-brand">{panel.title_highlight}</span>
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300" data-testid="auth-panel-subtitle">
              {panel.subtitle}
            </p>
            <ul className="mt-9 space-y-3">
              {(panel.bullets || []).map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-slate-200">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-brand" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function GoogleButton({ next }) {
  return (
    <button
      type="button"
      data-testid="google-login-btn"
      onClick={() => startGoogleLogin(next)}
      className="flex w-full items-center justify-center gap-3 rounded-full border border-line bg-white py-3.5 text-sm font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8z" />
        <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2a7 7 0 0 1-6.6-4.8H1.4v3.1A12 12 0 0 0 12 24z" />
        <path fill="#FBBC05" d="M5.4 14.5a7.2 7.2 0 0 1 0-4.6V6.8H1.4a12 12 0 0 0 0 10.4l4-2.7z" />
        <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.8l4 3.1A7 7 0 0 1 12 4.8z" />
      </svg>
      Continuer avec Google
    </button>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const next = location.state?.from || "/compte";

  useSeo({ title: "Connexion — ISMO-TECH", noIndex: true });

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(form.email, form.password);
      toast.success(`Bienvenue ${user.name?.split(" ")[0] || ""} !`);
      navigate(user.role === "admin" ? "/admin" : user.role === "vendor" ? "/vendeur/dashboard" : next);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Connexion"
      subtitle="Accédez à vos licences, commandes et factures."
      footer={
        <p className="mt-6 text-sm text-slate-500">
          Pas encore de compte ?{" "}
          <Link to="/inscription" data-testid="go-register" className="font-bold text-brand hover:underline">
            Créer un compte
          </Link>
        </p>
      }
    >
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
        {error && (
          <div data-testid="login-error" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Email</span>
          <span className="relative block">
            <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="login-email"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="h-12 w-full rounded-xl border border-line pl-11 pr-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
            />
          </span>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Mot de passe</span>
          <span className="relative block">
            <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="login-password"
              required
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="h-12 w-full rounded-xl border border-line pl-11 pr-11 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </span>
        </label>

        <button
          type="submit"
          data-testid="login-submit"
          disabled={loading}
          className="btn-glow flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-white disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Se connecter
        </button>

        <div className="flex items-center gap-3 py-1">
          <span className="h-px flex-1 bg-line" />
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">ou</span>
          <span className="h-px flex-1 bg-line" />
        </div>
        <GoogleButton next={next} />
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useSeo({ title: "Créer un compte — ISMO-TECH", noIndex: true });

  const submit = async (event) => {
    event.preventDefault();
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      setError("Numéro de téléphone invalide. Vérifiez l'indicatif du pays et le nombre de chiffres.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await register(form);
      toast.success("Compte créé avec succès !");
      navigate("/compte");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Créer mon compte"
      subtitle="Un compte pour retrouver vos licences à vie."
      footer={
        <p className="mt-6 text-sm text-slate-500">
          Déjà client ?{" "}
          <Link to="/connexion" data-testid="go-login" className="font-bold text-brand hover:underline">
            Se connecter
          </Link>
        </p>
      }
    >
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        {error && (
          <div data-testid="register-error" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Nom complet</span>
          <span className="relative block">
            <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="register-name"
              required
              minLength={2}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="h-12 w-full rounded-xl border border-line pl-11 pr-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
            />
          </span>
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Email</span>
            <input
              data-testid="register-email"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
            />
          </label>
          <PhoneField
            testId="register"
            label="Téléphone"
            value={form.phone}
            onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
          />
        </div>
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Mot de passe (6 caractères min.)
          </span>
          <input
            data-testid="register-password"
            required
            minLength={6}
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
          />
        </label>

        <button
          type="submit"
          data-testid="register-submit"
          disabled={loading}
          className="btn-glow flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-white disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          Créer mon compte
        </button>

        <div className="flex items-center gap-3 py-1">
          <span className="h-px flex-1 bg-line" />
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">ou</span>
          <span className="h-px flex-1 bg-line" />
        </div>
        <GoogleButton next="/compte" />
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  useSeo({ title: "Mot de passe oublié — ISMO-TECH", noIndex: true });

  const submit = async (event) => {
    event.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <AuthShell title="Mot de passe oublié" subtitle="Nous vous envoyons un lien de réinitialisation.">
      {sent ? (
        <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          Si un compte existe pour cet email, un lien de réinitialisation a été généré.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input
            data-testid="forgot-email"
            required
            type="email"
            placeholder="votre@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
          />
          <button
            type="submit"
            data-testid="forgot-submit"
            className="w-full rounded-full bg-brand py-4 text-sm font-bold text-white"
          >
            Envoyer le lien
          </button>
        </form>
      )}
    </AuthShell>
  );
}
